import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { ExpressServiceDataDocument } from '@packages/gql/generated/shopping/ExpressServiceDataDocument';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { StatusChip } from '@packages/shared/src/ui/StatusChip/StatusChip';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { TextWithDialog } from '@packages/modules/src/TextWithDialog/TextWithDialog.client';
import { expressServiceInfoMessages } from './messages';

/* GraphQL */ `
  query ExpressServiceData {
    deliveryInfo {
      deliveryDate
      countdown {
        hours
        minutes
        seconds
      }
    }
  }
`;

/**
 * This component renders a delivery day and a countdown for express service delivery.
 */
export const ExpressServiceInfo = () => {
  const { formatMessage, locale } = useIntl();
  const dispatchGTMEvent = useTracking();

  const [result, reexecuteQuery] = useQuery({
    query: ExpressServiceDataDocument,
    context: useMemo(() => ({ suspense: false }), []),
  });

  // Update the countdown on re-execute query
  useEffect(() => {
    if (result.fetching) return;

    // Set up to refetch every 60 seconds, if the query is idle
    const timerId = setTimeout(() => {
      reexecuteQuery({ requestPolicy: 'network-only' });
    }, 60000);

    // Clear the timer to prevent memory leaks
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timerId);
  }, [result.fetching, reexecuteQuery]);

  if (!result.data) {
    return <Skeleton className="h-4 w-full" />;
  }
  const { deliveryDate, countdown } = result.data.deliveryInfo;
  const handleDialogOpen = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'noteLayer',
      eventLabel: 'detailview',
      eventValue: '24-Stunden-Lieferung',
    });
  };

  return (
    <div>
      <div className="text-size-body-sm font-bold">
        {formatMessage(expressServiceInfoMessages.expressServiceDeliveryUntil, {
          day: new Date(deliveryDate).toLocaleString(locale, { weekday: 'long' }),
        })}
      </div>
      <div className="text-size-body-sm">
        {formatMessage(expressServiceInfoMessages.expressServiceDeliveryCountdown, {
          countdown: (
            <StatusChip color="info" key="status-chip">
              {`${countdown.hours}:${countdown.minutes}h`}
            </StatusChip>
          ),
          modal: (msg) => (
            <TextWithDialog
              key="express-service-info-dialog"
              message={msg.join(' ')}
              dialogTitle={formatMessage(expressServiceInfoMessages.modalHeadline)}
              onOpenDialogHandler={handleDialogOpen}
              dialogContent={<ServiceLayer serviceLayerId="delivery_24-stunden" />}
              displayTrackingProps={{ category: 'dv-service-layer-express-service-info' }}
            />
          ),
        })}
      </div>
    </div>
  );
};
